// The public cover catalog, from the cover library (owner, 2026-10-17):
// the ON/OFF switch, « Lien client » (copy) and one link per category.
//
// The switch also turns the agent's two Brain entries on/off with it — the saved
// reply « Catalogue des couvertures » ({lien_catalogue}) and the instruction that
// says when to send it — so the agent never sends a link that does not work.
// They are created the first time the catalog is switched on.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi, whatsappApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

export const CATALOG_URL = 'https://app.storiz.ma/couvertures';
export const BRAIN_REPLY_TITLE = 'Catalogue des couvertures';
export const BRAIN_RULE_TITLE = 'Catalogue des couvertures — quand l’envoyer';
const BRAIN_REPLY_BODY = 'Voici nos modèles de couverture 📖\n{lien_catalogue}\nTouchez celui qui vous plaît, puis « Je choisis ce modèle » : votre choix arrive directement ici.';
const BRAIN_RULE_BODY = 'Quand une cliente demande à voir des modèles, des couvertures, des exemples ou le catalogue : réponds avec use_template « Catalogue des couvertures » (le lien s’ouvre sur sa catégorie). N’écris jamais le lien toi-même. Si elle a déjà écrit « je veux la couverture … », ne renvoie pas le lien : sa couverture est enregistrée.';

async function syncBrain(on: boolean): Promise<void> {
  const { knowledge } = await whatsappApi.knowledge();
  const find = (title: string) => knowledge.find((k) => String(k.title).trim() === title);
  const reply = find(BRAIN_REPLY_TITLE);
  const rule = find(BRAIN_RULE_TITLE);
  if (on) {
    if (!reply) await whatsappApi.addKnowledge({ kind: 'template', title: BRAIN_REPLY_TITLE, body: BRAIN_REPLY_BODY, section: 'sales' });
    else if (!reply.enabled) await whatsappApi.updateKnowledge(reply.id, { enabled: true });
    if (!rule) await whatsappApi.addKnowledge({ kind: 'instruction', title: BRAIN_RULE_TITLE, body: BRAIN_RULE_BODY, section: 'sales' });
    else if (!rule.enabled) await whatsappApi.updateKnowledge(rule.id, { enabled: true });
  } else {
    if (reply?.enabled) await whatsappApi.updateKnowledge(reply.id, { enabled: false });
    if (rule?.enabled) await whatsappApi.updateKnowledge(rule.id, { enabled: false });
  }
}

export function CatalogBar({ categories, current }: { categories: Array<{ slug: string; label_fr?: string | null; icon?: string | null }>; current: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const { data } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get(), staleTime: 30_000 });
  const on = data?.settings?.cover_catalog_enabled === true;
  const toggle = useMutation({
    mutationFn: async () => {
      const next = !on;
      await settingsApi.update({ cover_catalog_enabled: next });
      await syncBrain(next);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); qc.invalidateQueries({ queryKey: ['assistant-knowledge'] }); },
  });
  const link = (slug?: string) => (slug ? `${CATALOG_URL}?cat=${slug}` : CATALOG_URL);
  const copy = async (url: string) => {
    try { await navigator.clipboard.writeText(url); setCopied(url); setTimeout(() => setCopied(null), 1800); } catch { window.prompt(t('Copiez le lien :'), url); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button type="button" role="switch" aria-checked={on} disabled={toggle.isPending}
        onClick={() => { if (on || window.confirm(t('Rendre le catalogue public ? Les clientes pourront l’ouvrir, et l’agent enverra le lien quand on lui demande des modèles.'))) toggle.mutate(); }}
        className={`h-9 px-3 rounded-lg text-[13px] font-semibold border ${on ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-white text-gray-700 border-gray-300'} disabled:opacity-50`}
        title={t('Catalogue public des couvertures')}>
        {on ? t('Catalogue public : ON') : t('Catalogue public : OFF')}
      </button>
      <div className="relative">
        <div className="flex">
          <button type="button" onClick={() => void copy(link(current || undefined))}
            className="h-9 px-3 rounded-l-lg border border-gray-300 bg-white text-[13px] font-semibold text-gray-800 hover:bg-gray-50">
            {copied ? t('✓ Lien copié') : t('🔗 Lien client')}
          </button>
          <button type="button" aria-label={t('Un lien par catégorie')} aria-expanded={menu} onClick={() => setMenu(!menu)}
            className="h-9 w-8 rounded-r-lg border border-l-0 border-gray-300 bg-white text-gray-600 hover:bg-gray-50">▾</button>
        </div>
        {menu && (
          <div className="absolute right-0 z-30 mt-1 w-64 rounded-xl border border-gray-200 bg-white shadow-lg p-1" role="menu">
            {[{ slug: '', label_fr: t('Toutes les couvertures'), icon: null as string | null }, ...categories].map((c) => (
              <button key={c.slug || 'all'} type="button" role="menuitem" onClick={() => { void copy(link(c.slug || undefined)); setMenu(false); }}
                className="w-full text-left px-3 py-2 rounded-lg text-[13px] text-gray-800 hover:bg-gray-50 flex items-center gap-2">
                {c.icon ? <span aria-hidden>{c.icon}</span> : null}<span className="flex-1">{c.label_fr ?? c.slug}</span><span className="text-[11px] text-gray-400">{t('copier')}</span>
              </button>
            ))}
            <a href={link(current || undefined)} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-lg text-[13px] text-brand-600 hover:bg-gray-50">{t('Ouvrir la page ↗')}</a>
          </div>
        )}
      </div>
      {toggle.isError && <span className="text-[12px] text-red-700">{t('Échec : {e}', { e: (toggle.error as Error).message })}</span>}
    </div>
  );
}
